import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import MainFooter from '../../../../../components/mazraeKit/footers/footerMain';
import ChatHeaderIcon from '../../../../../components/mazraeKit/headers/ChatHeaderIcon/ChatHeaderIcon';
import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import NotificationHeaderIcon from '../../../../../components/mazraeKit/headers/NotificationHeaderIcon/NotificationHeaderIcon';
import NoItemFoundPage from '../../../../../components/noItemFoundPage';
import SearchField from '../../../../../components/SearchField';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useGetMyStoreAds from '../../../../../services/hookService/getMyStoreAds/useGetMyStoreAds';
import useDebounce from '../../../../../utilities/hooks/useDebounce/useDebounce';
import TabSelector from '../../../../myAccount/my-orders/components/TabSelector';
import AdvertisementStatusController from './components/cards/AdvertisementStatusController';
import FilterAdsByInputGroupBottomSheet from './components/FilterAdsByInputGroupBottomSheet';
import IconActionButton from './components/IconActionButton';
import {manageMyStoreAdsTabsConfig} from './manageMyStoreAdsTabsConfig';

const ManageMyStoreAds = () => {
  const navigate = useNavigate();
  const {search} = useLocation();

  const searchParams = new URLSearchParams(search);
  const tabFromUrl = searchParams.get('tab') as Exclude<
    StatusList,
    'promoted'
  > | null;

  const isValidTab = manageMyStoreAdsTabsConfig.some(t => t.key === tabFromUrl);

  const [selectedTab, setSelectedTab] = useState<
    Exclude<StatusList, 'promoted'>
  >(isValidTab ? tabFromUrl! : 'published');

  const [filterAdType, setFilterAdType] = useState<string[]>([]);

  const [openFilterBottomSheet, setOpenFilterBottomSheet] =
    useState<boolean>(false);

  const [inputValue, setInputValue] = useState('');
  const debouncedValue = useDebounce<string>(inputValue, 1000);

  const {data: storeAds, isLoading} = useGetMyStoreAds({
    status: selectedTab,
    search: debouncedValue,
    advertisement_type: filterAdType,
  });

  // sync tab changes to URL and fill out the tab search param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', selectedTab);

    navigate({search: params.toString()}, {replace: true});

    // reset the filters when changing tabs
    setFilterAdType([]);
  }, [selectedTab]);

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      {/* Header - Always at top */}
      <CustomHeader
        title="آگهی های فروشگاه من"
        preIcon="icon-nameArrow---Right"
        isActiveRightIcon={true}
        backRoute="/my-store-info"
        postComponent={() => (
          <div className="flex gap-3">
            <NotificationHeaderIcon />
            <ChatHeaderIcon />
          </div>
        )}
      />

      {/* Main content area - Takes remaining space */}
      <div className="overflow-y-auto flex-1">
        <div className="px-6 py-3">
          <TabSelector
            tabsConfig={manageMyStoreAdsTabsConfig}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            inActiveClassName="text-gray-800 border-b-2 border-transparent "
            tabsOptionsClassName="text-md"
          />

          <div className="mt-3">
            <SearchField
              placeholder="جستجو در آگهی‌های فروشگاه"
              type="text"
              value={inputValue}
              onChange={value => setInputValue(value)}
            />
          </div>

          <div className="flex gap-3 py-3">
            <IconActionButton
              title="فیلتر کردن"
              onClick={() =>
                storeAds?.length! > 0 && setOpenFilterBottomSheet(true)
              }
              icon="icon-nameFilter"
              isActive={Boolean(filterAdType.length)}
            />
            {selectedTab === 'published' && (
              <IconActionButton
                title="ویرایش چند آگهی"
                onClick={() => navigate('/edit-bulk-store-ads')}
                icon="icon-nameEdit-Square"
                outline="green"
              />
            )}
          </div>

          {/* Content Area */}
          <div className="min-h-[calc(100vh-250px)]">
            {isLoading && (
              <div className="flex justify-center items-center h-64">
                <SpinnerLoading size={40} />
              </div>
            )}

            {!isLoading && !storeAds?.length && (
              <NoItemFoundPage className="h-[50dvh]" />
            )}

            {!isLoading && storeAds?.length! > 0 && (
              <div className="pb-24 space-y-3">
                {/* Extra padding for footer */}
                {storeAds?.map((storeAd: IGetMyStoreAdsData) => (
                  <AdvertisementStatusController
                    storeAd={storeAd}
                    status={selectedTab}
                    key={storeAd?.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 bg-white border-t">
        <MainFooter />
      </div>

      <FilterAdsByInputGroupBottomSheet
        shouldOpen={openFilterBottomSheet}
        setShouldOpen={setOpenFilterBottomSheet}
        filterAdType={filterAdType}
        setFilterAdType={setFilterAdType}
        status={selectedTab}
      />
    </div>
  );
};

export default ManageMyStoreAds;
